import { Component, EventEmitter, Input, Output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileSelectEvent, FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Badge } from 'primeng/badge';

type ValidationError = 'EMPTY' | 'INVALID_EXTENSION' | 'INVALID_MIME' | 'MAX_SIZE_EXCEEDED';

@Component({
  selector: 'lib-upload-zone',
  imports: [CommonModule, FileUpload, Button, Tooltip, Badge],
  templateUrl: './upload-zone.component.html',
  styleUrl: './upload-zone.component.css',
  standalone: true,
})
export class UploadZoneComponent {
  /**
   * Tipos de archivos permitidos
   * @default '.pdf,.xml,application/pdf,application/xml,text/xml'
   * @type string
   * */
  @Input() acceptFile = '.pdf,.xml,application/pdf,application/xml,text/xml';

  /**
   * Tamaño máximo por archivo en bytes
   * @default 10MB*/
  @Input() maxFileSize = 10 * 1024 * 1024;
  @Input() allowedExtensions: string[] = ['.pdf', '.xml'];

  /** Tipos MIME permitidos — ahora configurable */
  @Input() allowedMimeTypes: string[] = [
    // Imagenes
    'image/jpeg',
    'image/png',

    // PDF
    'application/pdf',

    // XML
    'application/xml',
    'text/xml',

    // TXT
    'text/plain',

    // CSV
    'text/csv',
    'application/csv',

    // Excel
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

    // Word
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  /** Permite seleccionar varios archivos */
  @Input() multiple = false;

  /** Deshabilita el componente */
  @Input() disabled = false;

  // Ocultar buttons
  @Input() hideUploadButton = false;

  /**
   * Texto personalizable */
  @Input() title = 'Subir archivos';
  @Input() helpText = 'Arrastra y suelta aquí tus archivos o haz clic para seleccionarlos.';
  @Input() footerNote = 'Permitidos: .pdf, .xml';

  /**
   * Eventos
   */
  @Output() filesReady = new EventEmitter<File[]>();
  @Output() validationFailed = new EventEmitter<{
    reason: ValidationError;
    invalid: File[];
  }>();
  isBusy = signal(false);
  @Output() filesCleared = new EventEmitter<void>();

  @ViewChild(FileUpload) fileUpload!: FileUpload;

  onSelect(e: FileSelectEvent) {

    if (this.hideUploadButton) {
      this.onUpload({
        files: e.files,
        originalEvent: e.originalEvent,
      } as FileUploadHandlerEvent);
    }
  }

  onUpload(event: FileUploadHandlerEvent) {
    const all = Array.from(event.files ?? []);


    if (!all.length) {
      console.log('❌ No hay archivos');
      this.validationFailed.emit({ reason: 'EMPTY', invalid: [] });
      return;
    }


    // Validar extensiones
    const invalidByExt = all.filter((f) => !this.isValidExtension(f.name));
    if (invalidByExt.length) {

      this.validationFailed.emit({
        reason: 'INVALID_EXTENSION',
        invalid: invalidByExt,
      });
      return;
    }

    // Validar MIME types
    const invalidByMime = all.filter((f) => !this.isValidMime(f.type));
    if (invalidByMime.length) {

      this.validationFailed.emit({
        reason: 'INVALID_MIME',
        invalid: invalidByMime,
      });
      return;
    }

    // Validar tamaño
    const tooBig = all.filter((f) => f.size > this.maxFileSize);
    if (tooBig.length) {

      this.validationFailed.emit({
        reason: 'MAX_SIZE_EXCEEDED',
        invalid: tooBig,
      });
      return;
    }

    this.filesReady.emit(all);
  }

  formatSize(size: number) {
    if (size === 0) return '0 B';
    const i = Math.floor(Math.log(size) / Math.log(1024));
    const v = (size / Math.pow(1024, i)).toFixed(2);
    return `${v} ${['B', 'KB', 'MB', 'GB', 'TB'][i]}`;
  }

  clearFiles() {
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  onClearFiles(clearCallback: () => void) {
    clearCallback();
    this.filesCleared.emit();
  }

  private isValidExtension(name: string): boolean {
    const dot = name.lastIndexOf('.');
    if (dot === -1) {
      return false;
    }
    const ext = name.slice(dot).toLowerCase();

    return this.allowedExtensions.map((e) => e.toLowerCase()).includes(ext);
  }

  private isValidMime(type: string): boolean {
    if (!type) return true;

    // Validamos contra los MIME types configurables del @Input
    return this.allowedMimeTypes.includes(type);
  }
}
